import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileSignature, RefreshCw, CalendarClock, Printer } from 'lucide-react';
import { formatDate } from '../../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const giorniA = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.slice(0, 10));
  return Math.ceil((d - new Date(new Date().toDateString())) / 86400000);
};

const BadgeRinnovi = ({ count, testid }) => {
  if (!count) return null;
  return (
    <Badge className="rounded-none bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300 text-[10px] font-mono inline-flex items-center gap-1" data-testid={testid}>
      <RefreshCw className="h-2.5 w-2.5" />
      Rinnovato {count} {count === 1 ? 'volta' : 'volte'}
    </Badge>
  );
};

/** Tab "Abbonamenti": contratti di assistenza + abbonamenti del cliente. */
export default function AbbonamentiTab({ clienteId, contratti, abbonamenti, isAdmin }) {
  const navigate = useNavigate();

  const stampaContrattoDaFirmare = async () => {
    try {
      const res = await axios.get(`${API}/contratti/da-firmare/${clienteId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contratto_da_firmare.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Contratto da firmare scaricato: dati cliente e dispositivi già compilati');
    } catch (e) {
      toast.error('Errore generazione contratto da firmare');
    }
  };

  // Prossima scadenza: la più vicina (futura se esiste, altrimenti l'ultima scaduta) tra abbonamenti attivi e contratti
  const scadenze = [
    ...abbonamenti.filter((a) => a.attivo && a.data_scadenza).map((a) => ({ data: a.data_scadenza.slice(0, 10), label: a.descrizione || 'Abbonamento', id: `abb-${a.id}` })),
    ...contratti.filter((c) => c.data_fine && !c.disdetta && (!c.data_inizio_fatturazione || c.data_inizio_fatturazione.slice(0, 10) <= new Date().toISOString().slice(0, 10))).map((c) => ({ data: c.data_fine.slice(0, 10), label: `Contratto assistenza n. ${c.anno}/${String(c.numero).padStart(3, '0')}`, id: `ctr-${c.id}` })),
  ].sort((a, b) => a.data.localeCompare(b.data));
  const prossima = scadenze[0] || null;
  const gg = prossima ? giorniA(prossima.data) : null;
  const tono = gg === null ? '' : gg < 0 ? 'border-destructive bg-destructive/10 text-destructive'
    : gg <= 30 ? 'border-red-500 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'
    : gg <= 60 ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400'
    : 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400';

  return (
    <>
      {prossima && (
        <div className={`border-2 p-3 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 ${tono}`} data-testid="prossima-scadenza-banner">
          <CalendarClock className="h-5 w-5 shrink-0" />
          <span className="text-xs font-mono uppercase tracking-wider">Prossima scadenza</span>
          <span className="text-lg font-black font-mono">{formatDate(prossima.data)}</span>
          <span className="text-xs">
            {gg < 0 ? `scaduta da ${-gg} giorni` : gg === 0 ? 'scade oggi' : `tra ${gg} giorni`} · {prossima.label}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-mono uppercase text-muted-foreground">Contratti di assistenza ({contratti.length})</p>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-none font-mono uppercase text-xs" onClick={stampaContrattoDaFirmare} data-testid="stampa-contratto-da-firmare-btn" title="PDF precompilato con dati cliente e dispositivi registrati, pronto per la firma">
              <Printer className="h-3 w-3 mr-1" />
              Stampa contratto da firmare
            </Button>
            <Button size="sm" className="rounded-none font-mono uppercase text-xs" onClick={() => navigate(`/contratti/nuovo?cliente_id=${clienteId}`)} data-testid="new-contratto-btn">
              <FileSignature className="h-3 w-3 mr-1" />
              Nuovo Contratto Assistenza
            </Button>
          </div>
        )}
      </div>
      {contratti.length > 0 && (
        <div className="border border-border mb-6">
          <table className="data-table">
            <thead>
              <tr>
                <th>N.</th>
                <th>Periodo</th>
                <th>Canone</th>
                <th>Pagamento</th>
                <th>Stato</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {contratti.map((c) => (
                <tr key={c.id} className={prossima?.id === `ctr-${c.id}` ? 'bg-amber-50 dark:bg-amber-950/20' : ''}>
                  <td className="font-mono">{c.anno}/{String(c.numero).padStart(3, '0')}</td>
                  <td className="text-xs">
                    {formatDate(c.data_inizio)} → <span className={prossima?.id === `ctr-${c.id}` ? 'font-bold' : ''}>{formatDate(c.data_fine)}</span>
                  </td>
                  <td className="font-mono">{(c.canone_annuo || 0).toFixed(2)} EUR</td>
                  <td className="text-xs">{c.modalita_pagamento || '-'}</td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge className={`rounded-none ${c.stato === 'firmato' ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}`}>
                        {c.stato}
                      </Badge>
                      <BadgeRinnovi count={c.rinnovi_taciti} testid={`badge-rinnovi-contratto-${c.id}`} />
                    </div>
                  </td>
                  <td>
                    <Button variant="ghost" size="sm" className="rounded-none text-xs" onClick={() => navigate(`/contratti/${c.id}`)} data-testid={`open-contratto-${c.id}`}>
                      Apri
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {abbonamenti.length === 0 ? (
        <p className="text-muted-foreground text-sm py-4">Nessun abbonamento registrato</p>
      ) : (
        <div className="border border-border">
          <table className="data-table">
            <thead>
              <tr>
                <th>Descrizione</th>
                <th>Importo</th>
                <th>Inizio</th>
                <th>Scadenza</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {abbonamenti.map((abb) => (
                <tr key={abb.id} className={prossima?.id === `abb-${abb.id}` ? 'bg-amber-50 dark:bg-amber-950/20' : ''}>
                  <td>{abb.descrizione}</td>
                  <td className="font-mono">{abb.importo?.toFixed(2)} EUR</td>
                  <td>{formatDate(abb.data_inizio)}</td>
                  <td>
                    <Badge variant="outline" className={`rounded-none font-mono text-xs ${prossima?.id === `abb-${abb.id}` ? 'border-amber-500 text-amber-700 dark:text-amber-400 font-bold' : ''}`}>
                      {formatDate(abb.data_scadenza)}
                    </Badge>
                  </td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge className={`rounded-none ${abb.attivo ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}`}>
                        {abb.attivo ? 'Attivo' : 'Scaduto'}
                      </Badge>
                      <BadgeRinnovi count={abb.rinnovi_effettuati} testid={`badge-rinnovi-abb-${abb.id}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
